import React, { Fragment, useEffect, useState } from 'react';
import VgsCollectValue from '../VgsInput/VgsCollectValue';
import useFieldTracker from '../../hooks/useFieldTracker';
import RawSelector from '../RawSelector';
import { STATES } from '../../const';
import { FieldError } from '../VgsInput/field';

export default function AddressCollect({
  entityName,
  onRegionChange,
  onInvalidChange,
  form,
  fieldErrors,
}: {
  entityName: string;
  onRegionChange: (region: string) => void;
  onInvalidChange: (fieldName: string, isInvalid: boolean) => void;
  form: VGSForm<unknown>;
  fieldErrors: FieldError[];
}): React.JSX.Element {
  const [emptyFields, onEmptyChange] = useFieldTracker([
    `${entityName}AddressStreet`,
    `${entityName}AddressCity`,
    `${entityName}AddressPostalCode`,
  ]);
  const [invalidFields, onFieldInvalidChange] = useFieldTracker([]);
  const [selectedRegion, setSelectedRegion] = useState(STATES[0].value);
  const [isValid, setIsValid] = useState(false);

  const formattedStates = STATES.map((s) => ({
    ...s,
    selected: s.value === selectedRegion,
  }));

  const sharedProps = {
    type: 'text' as VgsFieldType,
    onEmptyChange,
    onInvalidChange: onFieldInvalidChange,
    form,
    fieldErrors,
  };

  useEffect(() => {
    const newValidity = emptyFields.length === 0 && invalidFields.length === 0;
    if (newValidity !== isValid) {
      onInvalidChange(`${entityName}Address`, !newValidity);
      setIsValid(newValidity);
    }
  }, [emptyFields, entityName, invalidFields, isValid, onInvalidChange]);

  const onRegionSelectChange = (region?: string): void => {
    if (region === undefined) {
      return;
    }
    setSelectedRegion(region);
    onRegionChange(region);
  };

  return (
    <Fragment>
      <div className='md:flex mb-5 justify-between gap-2'>
        <div className='md:w-1/2 my-2'>
          <VgsCollectValue
            label='Street'
            name={`${entityName}AddressStreet`}
            required={true}
            showRequiredAsterisk={true}
            {...sharedProps}
          />
        </div>
        <div className='md:w-1/2 my-2'>
          <VgsCollectValue
            label='Unit'
            name={`${entityName}AddressUnit`}
            required={false}
            showRequiredAsterisk={false}
            {...sharedProps}
          />
        </div>
      </div>
      <div className='md:flex mb-5 justify-between gap-2'>
        <div className='md:w-1/3 my-2'>
          <VgsCollectValue
            label='City'
            name={`${entityName}AddressCity`}
            required={true}
            showRequiredAsterisk={true}
            {...sharedProps}
          />
        </div>

        <div className='md:w-1/3 my-2'>
          <RawSelector
            label='State'
            options={formattedStates}
            onChange={onRegionSelectChange}
          />
        </div>

        <div className='md:w-1/3 my-2'>
          <VgsCollectValue
            label='Zip Code'
            name={`${entityName}AddressPostalCode`}
            required={true}
            showRequiredAsterisk={true}
            maskInput={{
              mask: 'XXXXX',
              maskChar: null,
              formatChar: { X: '[0-9]' },
            }}
            validations={[/^\d{5}$/]}
            {...sharedProps}
          />
        </div>
      </div>
    </Fragment>
  );
}
